import { Endpoint } from "shared/utils/endpoints";
import { HTTP_CLIENT } from "shared/utils/interceptor";

export enum ProjectDesignerRole {
  DESIGNER = "designer",
  FINAL_REVIEWER = "final_reviewer",
  BOTH = "both",
}

export function formatProjectDesignerRole(role: string): string {
  return role
    .split("_")
    .map(
      (word) =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
}

interface ProjectListParams {
  page: number;
  limit: number;
  search?: string;
  status?: string;
  revision_status?: string;
  sort_order?: "ASC" | "DESC";
  is_joined?: boolean;
  only_my_projects?: boolean;
  is_archived?: boolean;
}

const ProjectListService = ({
  page,
  limit,
  search,
  status,
  revision_status,
  sort_order,
  is_joined,
  only_my_projects,
  is_archived,
}: ProjectListParams) => {
  const params: any = { page, limit };

  if (search) params.search = search;
  if (status) params.status = status;
  if (revision_status) params.revision_status = revision_status;
  if (sort_order) params.sort_order = sort_order;
  if (is_joined === true) params.is_joined = true;
  if (only_my_projects === true) params.only_my_projects = true;
  if (is_archived === true) params.is_archived = true;

  return HTTP_CLIENT.get(Endpoint.project.list, { params });
};

const GetClientOnboardingProjectService = () => {
  return HTTP_CLIENT.get(Endpoint.project.clientOnboardingProject);
};

const ProjectDetailService = (project_id: number) => {
  return HTTP_CLIENT.get(
    Endpoint.project.projectDetail.replace(
      ":project_id",
      project_id.toString(),
    ),
  );
};

const DismissReadyToStartService = (project_id: number) => {
  return HTTP_CLIENT.post(
    Endpoint.project.dismissReadyToStart.replace(
      ":project_id",
      project_id.toString(),
    ),
  );
};

const ProjectExtendedDetailService = (project_id: number) => {
  return HTTP_CLIENT.get(
    Endpoint.project.projectExtendedDetail.replace(
      ":project_id",
      project_id.toString(),
    ),
  );
};

const ProjectOnboardingService = (project_id: number, payload: FormData) => {
  return HTTP_CLIENT.patch(
    Endpoint.project.projectOnboarding.replace(
      ":project_id",
      project_id.toString(),
    ),
    payload,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

const ProjectNextStatusService = (project_id: number) => {
  return HTTP_CLIENT.patch(
    Endpoint.project.projectNextStatus.replace(
      ":project_id",
      project_id.toString(),
    ),
  );
};

const ProjectCompleteService = (project_id: number) => {
  return HTTP_CLIENT.patch(
    Endpoint.project.projectComplete.replace(
      ":project_id",
      project_id.toString(),
    ),
    { status: "completed" },
  );
};

const ProjectSelectionService = (only_active?: boolean, valid_for_upload?: boolean) => {
  const params: any = {};
  if (only_active !== undefined) params.only_active = only_active;
  if (valid_for_upload !== undefined) params.valid_for_upload = valid_for_upload;
  return HTTP_CLIENT.get(Endpoint.project.projectSelection, { params });
};

const ProjectDocumentDownloadService = (project_id: number, type: string) => {
  return HTTP_CLIENT.get(
    Endpoint.project.projectDocumentDownload.replace(
      ":project_id",
      project_id.toString(),
    ),
    {
      params: { type },
      responseType: "blob",
    },
  );
};

const AddProjectDesignerService = (
  project_id: number,
  role: ProjectDesignerRole = ProjectDesignerRole.DESIGNER,
) => {
  return HTTP_CLIENT.post(
    Endpoint.project.addProjectDesigner.replace(
      ":project_id",
      project_id.toString(),
    ),
    { role },
  );
};

const LeaveProjectDesignerService = (project_id: number) => {
  return HTTP_CLIENT.delete(
    Endpoint.project.leaveProjetcDesigner.replace(
      ":project_id",
      project_id.toString(),
    ),
  );
};

const ProjectArchiveService = (project_id: number, archive: boolean) => {
  return HTTP_CLIENT.patch(
    Endpoint.project.projectArchive.replace(
      ":project_id",
      project_id.toString(),
    ),
    { archive },
  );
};

const ProjectCollaboratorsService = (project_id: number) => {
  return HTTP_CLIENT.get(
    Endpoint.project.projectCollaborators.replace(
      ":project_id",
      project_id.toString(),
    ),
  );
};

export {
  ProjectListService,
  GetClientOnboardingProjectService,
  ProjectDetailService,
  DismissReadyToStartService,
  ProjectExtendedDetailService,
  ProjectOnboardingService,
  ProjectNextStatusService,
  ProjectCompleteService,
  ProjectSelectionService,
  ProjectDocumentDownloadService,
  AddProjectDesignerService,
  LeaveProjectDesignerService,
  ProjectArchiveService,
  ProjectCollaboratorsService,
};
